import { AlertTriangle, CheckCircle2, RefreshCw, XCircle } from 'lucide-react'
import type { ReactNode } from 'react'

import { Button, DiagnosticCopy } from '@krp/ui-kit'

import '../../../shared/uc1b/journals.css'
import { QueryState } from '../../../shared/uc1b/QueryState'
import type { ExchangeItemVm, ExchangeLineVm, ExchangeTone, SaleExchangeActions, SaleExchangeVm } from '../model/types'
import './exchange.css'

const TONE_ICON: Readonly<Record<ExchangeTone, ReactNode>> = {
  ok: <CheckCircle2 size={16} aria-hidden="true" />,
  warning: <AlertTriangle size={16} aria-hidden="true" />,
  danger: <XCircle size={16} aria-hidden="true" />,
}

/** Тело запроса или ответа: строки о кассире выделены, текст выделяется и копируется руками. */
function Body({ title, lines, cutText }: { title: string; lines: readonly ExchangeLineVm[]; cutText: string | null }) {
  return (
    <section className="aqsi-exchange__body" aria-label={title}>
      <h4>{title}</h4>
      <pre tabIndex={0}>
        {lines.map((line, index) => (
          <span key={index}>
            {line.cashier ? <mark>{line.text}</mark> : line.text}
            {index < lines.length - 1 ? '\n' : ''}
          </span>
        ))}
      </pre>
      {cutText !== null && (
        <p className="aqsi-journal__note" data-tone="warning">
          <AlertTriangle size={16} aria-hidden="true" />
          <span>{cutText}</span>
        </p>
      )}
    </section>
  )
}

function Exchange({ item }: { item: ExchangeItemVm }) {
  return (
    <li className="aqsi-exchange__item" data-tone={item.tone}>
      <div className="aqsi-exchange__head">
        <h3>{item.titleText}</h3>
      </div>
      {item.relationText !== null && <p className="aqsi-journal__note">{item.relationText}</p>}
      <div className="aqsi-exchange__meta">
        <span>Время: {item.sentText}</span>
        <span>Ответ через {item.durationText}</span>
        {item.facts.map((fact) => (
          <span key={fact}>{fact}</span>
        ))}
      </div>
      <p className="aqsi-exchange__status" role="status">
        {TONE_ICON[item.tone]}
        <span>{item.statusText}</span>
      </p>
      {item.failureText !== null && (
        <p className="aqsi-journal__note" data-tone="warning">
          <AlertTriangle size={16} aria-hidden="true" />
          <span>{item.failureText}</span>
        </p>
      )}
      {item.cashierText !== null && <p className="aqsi-exchange__cashier">{item.cashierText}</p>}
      <div className="aqsi-exchange__bodies">
        <Body title={item.requestTitle} lines={item.requestLines} cutText={item.requestCutText} />
        {item.responseText === null ? (
          <section className="aqsi-exchange__body" aria-label={item.responseTitle}>
            <h4>{item.responseTitle}</h4>
            <p className="aqsi-journal__note">Тела ответа нет.</p>
          </section>
        ) : (
          <Body title={item.responseTitle} lines={item.responseLines} cutText={item.responseCutText} />
        )}
      </div>
      <DiagnosticCopy text={item.reportText} label="Скопировать запрос и ответ" />
    </li>
  )
}

/**
 * Окно «Обмен с AQSI» продажи (решение владельца 04.11.2026): каждый запрос службы к кабинету по этой продаже — время, вид, что ушло (с
 * выделенным кассиром), код и тело ответа кабинета дословно. По нему видно, на чьей стороне ошибка. Только чтение: отсюда в кабинет ничего не
 * отправляется. Экран получает vm и actions и больше ничего.
 */
export function SaleExchangeScreen({ vm, actions }: { vm: SaleExchangeVm; actions: SaleExchangeActions }) {
  return (
    <section className="aqsi-journal" aria-labelledby="aqsi-exchange-title">
      <header className="aqsi-journal__head">
        <h2 id="aqsi-exchange-title">Обмен с AQSI</h2>
        <Button variant="ghost" icon={<RefreshCw size={16} aria-hidden="true" />} onClick={actions.reload}>
          Обновить
        </Button>
      </header>
      <p className="aqsi-journal__note">
        Продажа {vm.saleId}. Всё, что программа отправила в AQSI и прочитала у него по этой продаже, ответы AQSI и его уведомления — как есть, по
        времени, старые сверху. Рядом по времени — команды этой кассы без продажи (проверка связи, смена) и уведомления AQSI, которых нет ни у одной
        продажи. JSON показан с отступами, значения не изменены; «Скопировать» копирует тела ровно как отправлены и получены.
      </p>
      <QueryState query={vm.query} emptyText="По этой продаже программа ещё ничего не отправляла в AQSI" onRetry={actions.reload} />
      {vm.incompleteText !== null && (
        <p className="aqsi-journal__note" data-tone="warning">
          <AlertTriangle size={16} aria-hidden="true" />
          <span>{vm.incompleteText}</span>
        </p>
      )}
      {vm.items.length > 0 && (
        <>
          <DiagnosticCopy text={vm.reportText} label="Скопировать весь обмен" />
          <ol className="aqsi-journal__list" aria-label="Запросы к AQSI">
            {vm.items.map((item) => (
              <Exchange key={item.id} item={item} />
            ))}
          </ol>
        </>
      )}
      <div className="aqsi-journal__actions">
        <Button onClick={actions.close}>Закрыть</Button>
      </div>
    </section>
  )
}
